import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useCookieConsent } from "@/stores/useCookieConsent";
import { COOKIE_CATEGORIES, type CookieCategory, DEFAULT_CONSENT } from "@/types/cookie-consent";

export function CookiePreferencesModal() {
  const { t } = useTranslation();
  const { showPreferences, closePreferences, consent, acceptAll, rejectAll, setConsent } =
    useCookieConsent();

  // ローカル状態（保存前のプレビュー）
  const [localConsent, setLocalConsent] = useState(consent ?? DEFAULT_CONSENT);

  // consent が更新されたらローカル状態も更新
  useEffect(() => {
    setLocalConsent(consent ?? DEFAULT_CONSENT);
  }, [consent]);

  const handleToggle = (category: CookieCategory, enabled: boolean) => {
    setLocalConsent((prev) => ({ ...prev, [category]: enabled }));
  };

  const handleSave = () => {
    setConsent(localConsent);
  };

  return (
    <Dialog open={showPreferences} onOpenChange={closePreferences}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto duration-150">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            {t("cookieConsent.preferences.title")}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t("cookieConsent.preferences.description")}
          </DialogDescription>
        </DialogHeader>

        {/* カテゴリリスト */}
        <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
          {COOKIE_CATEGORIES.map((category) => (
            <div key={category.id} className="space-y-2">
              <div className="flex items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold flex flex-wrap items-center gap-2">
                    {t(category.nameKey)}
                    {category.required && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        ({t("cookieConsent.preferences.required")})
                      </span>
                    )}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1 break-words">
                    {t(category.descriptionKey)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 break-words">
                    {t("cookieConsent.preferences.examplesPrefix")}
                    {category.examples.join(", ")}
                  </p>
                </div>

                <Switch
                  checked={localConsent[category.id]}
                  onCheckedChange={(checked) => handleToggle(category.id, checked)}
                  disabled={category.required}
                  aria-label={`${t(category.nameKey)}を${
                    localConsent[category.id] ? "無効化" : "有効化"
                  }`}
                  className="flex-shrink-0"
                />
              </div>
            </div>
          ))}
        </div>

        {/* フッターリンク */}
        <div className="flex gap-4 text-sm border-t pt-4">
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {t("cookieConsent.preferences.privacyPolicyLink")}
          </a>
        </div>

        {/* アクションボタン */}
        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={rejectAll} className="sm:flex-1">
            {t("cookieConsent.preferences.rejectAll")}
          </Button>
          <Button variant="outline" onClick={handleSave} className="sm:flex-1">
            {t("cookieConsent.preferences.saveSelection")}
          </Button>
          <Button variant="default" onClick={acceptAll} className="sm:flex-1">
            {t("cookieConsent.preferences.acceptAll")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
